import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { VAT_RATE } from "@/data/menu";

const CartContext = createContext(null);
const LS_KEY = "meritz_cart_v1";

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null); // item being viewed in details modal

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(items));
    } catch {
      // ignore quota / privacy mode failures
    }
  }, [items]);

  const addItem = useCallback((item, qty = 1) => {
    setItems((prev) => {
      const key = item.name;
      const existing = prev.find((p) => p.name === key);
      if (existing) {
        return prev.map((p) =>
          p.name === key ? { ...p, qty: p.qty + qty } : p,
        );
      }
      return [
        ...prev,
        { name: item.name, desc: item.desc || "", price: item.price, qty },
      ];
    });
  }, []);

  const updateQty = useCallback((name, qty) => {
    setItems((prev) => {
      if (qty <= 0) return prev.filter((p) => p.name !== name);
      return prev.map((p) => (p.name === name ? { ...p, qty } : p));
    });
  }, []);

  const removeItem = useCallback((name) => {
    setItems((prev) => prev.filter((p) => p.name !== name));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const openCart = useCallback(() => setDrawerOpen(true), []);
  const closeCart = useCallback(() => setDrawerOpen(false), []);

  const openDetails = useCallback((item) => setDetailItem(item), []);
  const closeDetails = useCallback(() => setDetailItem(null), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const vat = Math.round(subtotal * VAT_RATE);
    const total = subtotal + vat;
    const count = items.reduce((s, i) => s + i.qty, 0);
    return { subtotal, vat, total, count };
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      totals,
      drawerOpen,
      openCart,
      closeCart,
      detailItem,
      openDetails,
      closeDetails,
    }),
    [items, totals, drawerOpen, detailItem, addItem, updateQty, removeItem, clearCart, openCart, closeCart, openDetails, closeDetails],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
